import { useEffect, useRef, useState } from "react";
import { bool, func, node } from "prop-types";
import { useSnackbar } from "notistack";
import { applyPatchExtended, applyCommentStorePatch } from "@repo/structure";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  CoreCommands,
  ProjectEditorCommands,
  EditorSocketContext,
  ProjectCommands,
} from "@client";
import { editor } from "@paths";
import {
  createCustomWorkTypeQuery,
  createProjectVersionComment,
  editProjectVersionComment,
  fetchGroupedProjectVersionCommentsQuery,
  moveProjectVersionComment,
  removeProjectVersionComment,
  removeProjectVersionCommentByPath,
  renameProjectVersionCommentElementQuery,
  saveProjectCoverQuery
} from "@query";
import { PROJECT_STATUS, SNACKBAR_WEBSOCKET_RESTORED } from "@utils";
import { useCheckEstimateEditorAccess, useStores, useWorkspaceWebsocket } from "@hooks";
import { useEstimateEditorStore, useEstimateComments } from "@tools"
import { drawUserMovement } from "./utils/drawUserMovement";

export const EditorSocketProvider = ({
  children,
  reloadProjectData,
  proposalSocket,
}) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { userStore, stateStore } = useStores();
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  const {
    init,
    socket,
    connectionOnline,
    sendAction,
    requestProjectRename,
    requestWorkTypeCreate,
    requestWorkTypeChange,
    requestLabelCreate: requestCompanyLabelCreate,
    requestLabelRemove: requestCompanyLabelRemove,
    setupWorkspaceEvents,
  } = useWorkspaceWebsocket();
  
  const { hasSellerPrivileges } = useCheckEstimateEditorAccess(editorStore.estimateUsers);
  
  const [isSetup, setSetupStatus] = useState(false);
  const [users, setUsers] = useState([]);
  const [hasConnectionError, setConnectionError] = useState(false);
  const [joinEdition, setJoinEdition] = useState(false);
  const [isXlsImportOngoing, setXlsImportOngoing] = useState(false);
  const editionPrivileges = useRef({
    joinEdition: false,
    joinComments: false
  });

  const lastSentAction = useRef(null);
  const projectJoined = useRef(null);
  const connectionOnlineSnackbar = useRef(null);
  const connectionRestoredSnackbar = useRef(null);
  const rememberScrollRef = useRef(null);
  
  // const { hasEditorPrivileges } = useCheckEstimateEditorAccess(editorStore.estimateUsers);
  
  // --------- connections ---------
  
  const onProjectJoinError = ({ message }) => {
    console.error("ERROR WHILE JOINING PROJECT: ", message)
    stateStore.setProjectCreatorProgressError(true, message);
  }
  
  const onProjectCreatorProgress = ({ progress, stepName }) => {
    console.log("CREATOR PROGRESS: ", progress);
    stateStore.setProjectCreatorProgress(progress, stepName);
  }
  
  const joinProject = (projectUuid, projectHash, isCreated, versionKey, rememberScroll) => {
    setJoinEdition(true);
    rememberScrollRef.current = rememberScroll;
    editionPrivileges.current = {
      joinEdition: true,
      // joinComments: hasEditorPrivileges && projectHash
      joinComments: Boolean(projectHash),
    }
    
    console.log("JOINING PROJECT", projectUuid);
    
    setupProjectEvents(
      true,
      true,
      // hasEditorPrivileges && projectHash,
      Boolean(projectHash),
    );
    
    socket.current.emit(CoreCommands.JOIN_PROJECT, {
      projectUuid,
      versionKey,
      newProject: !isCreated,
      commentHash: projectHash,
      userId: userStore.data.id,
      userUuid: userStore.data.uuid,
      name: userStore.data.fullname,
      email: userStore.data.email,
      avatar: userStore.data.avatar,
      ...editionPrivileges.current,
    });
    projectJoined.current = { projectUuid, projectHash };
  };
  
  const closeEstimate = () => {
    if(!projectJoined.current)
      return;
    console.log("CLOSING PROJECT");
    setupProjectEvents(false);
    editionPrivileges.current = undefined;
    socket.current.emit(CoreCommands.CLOSE_PROJECT, {
      projectUuid: projectJoined.current.projectUuid,
      commentHash: projectJoined.current.projectHash,
      userId: userStore.data.id,
      userUuid: userStore.data.uuid,
      ...editionPrivileges.current,
    });
    projectJoined.current = null;
    
    lastSentAction.current = "END";
    closeSnackbar(connectionOnlineSnackbar.current);
    closeSnackbar(connectionRestoredSnackbar.current);
    setUsers([]);
  };
  
  const setupProjectEvents = (on, openEditionChannel, openCommentsChannel) => {
    if(!on) {
      socket.current.removeAllListeners();
      setupWorkspaceEvents(true);
      return;
    }
    
    // socket.current.on(CoreCommands.CONNECT_ERROR, onDisconnect);
    // socket.current.on(CoreCommands.DISCONNECT, onDisconnect);
    socket.current.on(CoreCommands.ACTION_ERROR, onError);
    socket.current.on(ProjectEditorCommands.USER_JOINED, onJoined);
    socket.current.on(ProjectEditorCommands.USER_LEFT, onLeft);
    // socket.current.on(CoreCommands.CONNECTION, onReconnect);
    // socket.current.on(CoreCommands.RECONNECTION, onReconnect);
    socket.current.on(CoreCommands.JOIN_PROJECT, onProjectJoinError); //only join errors will come on this channel
    socket.current.on(ProjectCommands.PROJECT_PROGRESS, onProjectCreatorProgress);
    socket.current.on(ProjectCommands.PROJECT_WAS_CREATED, onProjectCreationEnd);
    
    if(openEditionChannel || !on) {
      socket.current.on(CoreCommands.WORKTYPE_ADD, onAddNewWorkType);
      socket.current.on(CoreCommands.WORKTYPE_EDIT, onWorkTypeChange);
      socket.current.on(CoreCommands.WORKTYPE_REMOVE, onWorkTypeRemove);
      
      socket.current.on(ProjectEditorCommands.USER_MOVE, drawUserMovement);
      socket.current.on(ProjectEditorCommands.PATCH, onStructurePatch);
      
      socket.current.on(ProjectEditorCommands.VERSION_LIST, onReceiveVersionList);
      socket.current.on(ProjectEditorCommands.VERSION_REQUEST, onReceiveVersion);
      socket.current.on(ProjectEditorCommands.VERSION_NEW, onCreateVersion);
      socket.current.on(ProjectEditorCommands.VERSION_REMOVE, onRemoveVersion);
      socket.current.on(ProjectEditorCommands.VERSION_RENAME, onRenameVersion);
      socket.current.on(ProjectEditorCommands.VERSION_ORDER, onReorderVersion);
      socket.current.on(ProjectEditorCommands.VERSION_LOCK, onVersionLock);
      socket.current.on(ProjectEditorCommands.VERSION_VISIBILITY, onVersionVisibility);
      socket.current.on(ProjectEditorCommands.VERSIONS_WORK_TYPE_UPDATE, onVersionWorkTypesUpdate);
      
      socket.current.on(ProjectEditorCommands.PERMIT_ADD, onWorkTypeAssignment);
      socket.current.on(ProjectEditorCommands.PERMIT_REMOVE, onWorkTypeRevoke);
      socket.current.on(ProjectEditorCommands.PERMIT_STATUS, onWorkTypeStatus);
      socket.current.on(ProjectEditorCommands.PERMIT_USER_VISIBILITY, onUserWorkTypesStatus);
      socket.current.on(ProjectEditorCommands.COVER_ADD, onCoverAdd);
      socket.current.on(ProjectEditorCommands.COVER_REMOVE, onCoverRemove);
      socket.current.on(ProjectEditorCommands.COVER_PAGES, onCoverUsedPagesUpdate);
      socket.current.on(ProjectEditorCommands.COVER_SCALE, onCoverScaleUpdate);
      socket.current.on(ProjectEditorCommands.SHARE_SETTINGS, onShareSettingsUpdate);
      
      socket.current.on(ProjectCommands.PROJECT_METADATA, onProjectMetadataUpdate);
      
      socket.current.on(ProjectCommands.USER_ADD, onProjectUserAdd);
      socket.current.on(ProjectCommands.USER_REMOVE, onProjectUserRemove);
      socket.current.on(ProjectCommands.USER_PROMOTE, onProjectUserPromote);
      
      socket.current.on(ProjectCommands.PROJECT_RENAME, onProjectRename);
      socket.current.on(ProjectCommands.PROJECT_REMOVE, onProjectRemove);
      socket.current.on(ProjectCommands.PROJECT_ARCHIVE, onProjectArchive);
      socket.current.on(ProjectCommands.PROJECT_STATUS, onProjectStatusUpdate);
      
      socket.current.on(ProjectEditorCommands.GENERATOR_START, onProjectAiGeneratorStart);
      socket.current.on(ProjectEditorCommands.GENERATOR_END, onProjectAiGeneratorEnd);
      socket.current.on(ProjectEditorCommands.GENERATOR_REASONING, onProjectAiGeneratorReasoning);
      
      socket.current.on(ProjectEditorCommands.AGENT_SESSION_START, onAgentSessionStart);
      socket.current.on(ProjectEditorCommands.AGENT_SESSION_END, onAgentSessionEnd);
      
      socket.current.on(ProjectEditorCommands.LABEL_CREATE, onProjectLabelCreate);
      socket.current.on(ProjectEditorCommands.LABEL_REMOVE, onProjectLabelRemove);
      socket.current.on(CoreCommands.LABEL_REMOVE, onCompanyLabelRemove);
      socket.current.on(CoreCommands.LABEL_CREATE, onCompanyLabelCreate);
      socket.current.on(CoreCommands.LABEL_UPDATE, onLabelUpdate);
    }
    if(openCommentsChannel || !on) {
      socket.current.on(ProjectEditorCommands.COMMENT_PATCH, onCommentPatch);
      
      // socket.current.on(ProjectEditorCommands.COMMENT_ADD, onCommentAdd);
      // socket.current.on(ProjectEditorCommands.COMMENT_EDIT, onCommentEdit);
      // socket.current.on(ProjectEditorCommands.COMMENT_REMOVE, onCommentRemove);
      // socket.current.on(ProjectEditorCommands.COMMENT_REMOVE_PATH, onCommentPathRemove);
      // socket.current.on(ProjectEditorCommands.COMMENT_MOVE, onCommentMove);
      // socket.current.on(ProjectEditorCommands.COMMENT_ELEMENT_RENAME, onCommentPlaceElementRename);
    }
    
    setSetupStatus(true);
  }

  const resetProjectData = async () => {
    if(!projectJoined.current) return;
    const { hash, estimateData } = await reloadProjectData();
    socket.current.emit(CoreCommands.JOIN_PROJECT, {
      projectUuid: estimateData.uuid,
      versionKey: editorStore.currentVersionKey,
      commentHash: hash,
      userId: userStore.data.id,
      userUuid: userStore.data.uuid,
      name: userStore.data.fullname,
      email: userStore.data.email,
      avatar: userStore.data.avatar,
      ...editionPrivileges.current,
    });
    projectJoined.current = { projectUuid: estimateData.uuid, projectHash: hash };
  }
  
  useEffect(() => {
    if(isSetup && connectionOnline) {
      onReconnect();
    }
    
    if(!connectionOnline) {
      onDisconnect();
    }
  }, [connectionOnline]);

  const onReconnect = async () => {
    console.log("EDITOR RECONNECTED");
    if(projectJoined.current && editionPrivileges.current?.joinEdition) {
      // the agent session may have ended while offline; the server sends it again on join when it lasts
      editorStore.setAgentSession(null);
      await resetProjectData();
    }
    
    if(connectionOnlineSnackbar.current) {
      closeSnackbar(connectionOnlineSnackbar.current);
      connectionRestoredSnackbar.current = enqueueSnackbar(
        t("alerts.editor.connection_restored"),
        {
          key: SNACKBAR_WEBSOCKET_RESTORED,
          variant: "success",
          autoHideDuration: 2000
        }
      );
    }
  };

  const onDisconnect = () => {
    console.log("EDITOR DISCONNECTED");
    if(lastSentAction.current !== "END" && editionPrivileges.current?.joinEdition) {
      closeSnackbar(connectionRestoredSnackbar.current);
      connectionOnlineSnackbar.current = enqueueSnackbar(
        t("alerts.editor.connection_offline"),
        {
          persist: true,
          variant: "warning"
        }
      );
    }
    setUsers([]);
  };

  const madeIdenticalAction = (command, payload, additionalChecks) => {
    if (!lastSentAction.current) return false;
    return (
      lastSentAction.current.command === command &&
      lastSentAction.current.timestamp > payload.timestamp &&
      (!additionalChecks || additionalChecks(lastSentAction.current.payload))
    );
  };

  const saveAndSendAction = (command, payload) => {
    if (!socket.current?.connected)
      return;
    
    if (editorStore.isArchived && command !== ProjectEditorCommands.VERSION_REQUEST)
      return;
    const timestamp = new Date().getTime();
    payload.timestamp = timestamp;
    payload.projectUuid = projectJoined.current.projectUuid;
    payload.projectHash = projectJoined.current.projectHash;
    lastSentAction.current = { command, payload, timestamp };
    sendAction(command, payload);
  };
  
  const navigateAndScroll = (versionKey) => {
    navigate(editor(projectJoined.current.projectUuid, versionKey), { replace: true });
    
    stateStore.scrollRef?.scrollTo({
      top: rememberScrollRef.current
        ? editorStore.versionScroll[editorStore.currentVersionKey] || 0
        : 0
    });
    
    rememberScrollRef.current = undefined;
  }

  // --------- remote actions ---------

  const onJoined = (userData) => {
    console.log("USER JOINED");
    setUsers((l) => [...l, ...userData])
  };

  const onLeft = (userSocketIds) => {
    console.log("USER LEFT");
    setUsers((l) => l.filter((u) => !userSocketIds.includes(u.socketId)));
  };
  
  const onProjectCreationEnd = ({ status }) => {
    editorStore.setCreatedStatus(status);
  }
  
  const onError = (data) => {
    console.error("ERROR => ", data.message);
    setConnectionError(true);
  }
  
  const onStructurePatch = (data) => {
    
    const structure = editorStore.estimateVersions.find(
      (v) => v.key === data.versionKey
    )?.structure;
    
    if (structure) try {
      structure.historyManager.withoutUndo(() => {
        data.patches.forEach(p => {
          if(p.removesElement && p.identifier)
            structure.historyManager.clearByTargetId(p.identifier);
          
          if(p.op !== "comment" && p.op !== "commentName")
            applyPatchExtended(structure, p);
        })
      })
    } catch(e) {
      console.log("Error: onStructurePatch", e);
    }
  };
  
  const onCommentPatch = (data) => {
    try {
      commentStore.historyManager.withoutUndo(() => {
        applyCommentStorePatch(commentStore, data.patches);
      });
    } catch(e) {
      console.log("Error: onCommentPatch", e);
    }
  }

  const onReceiveVersionList = ({ message, versions, projectMetadata }) => {
    if(message)
      console.error(message);
    if(versions) {
      console.log("VERSION LIST RECEIVED: ", versions?.map(v => v.key))
      editorStore.setVersions(versions);
    }

    console.log(projectMetadata?.workTypes.length);

    if(projectMetadata?.workTypes.length)
        editorStore.addCustomEstimateWorkTypes(projectMetadata.workTypes);
    if(projectMetadata?.labels.length)
        editorStore.addUnlistedLabels(projectMetadata.labels);
  }
  
  const onReceiveVersion = ({ message, versionData, reason }) => {
    if(message)
      console.error(message);
    if(versionData) {
      console.log("VERSION RECEIVED: ", versionData.key, reason || "")
      editorStore.setVersionData(versionData, !proposalSocket && !hasSellerPrivileges );
      // a version changed outside the editor keeps the user where they are
      if(reason !== "external_update")
        navigateAndScroll(versionData.key);
    }
    
    stateStore.resetProjectCreatorProgress();
  };

  const onCreateVersion = async ({ versionData, userSocketId, fromXls }) => {
    editorStore.addNewVersion(versionData, userSocketId === socket.current.id);
    navigateAndScroll(versionData.key);
    if(fromXls)
      setXlsImportOngoing(false);
    
    const groupedComments = await fetchGroupedProjectVersionCommentsQuery(
      projectJoined.current.projectUuid,
      userStore.userUuid,
      versionData.key
    );
    commentStore.addGroups(groupedComments);
  };

  const onRemoveVersion = ({ versionKey, newVersionData, versionName, userSocketId }) => {
    if(newVersionData && editorStore.currentVersion === versionKey) {
      editorStore.setVersionData(newVersionData, !proposalSocket && !hasSellerPrivileges);
      // navigateAndScroll(newVersionData.key);
      if(socket.current.id !== userSocketId)
        enqueueSnackbar(
          <p>
            {t("common.version")}
            <b>{` ${versionName} `}</b>
            {t("alerts.editor.version_removed")}
          </p>,
          { variant: "warning", autoHideDuration: 2000 }
        );
    }
    editorStore.removeSelectedVersion(versionKey);
  };

  const onRenameVersion = (data) => {
    if (
      madeIdenticalAction(
        ProjectEditorCommands.VERSION_RENAME,
        data,
        (p) => p.versionKey === data.versionKey
      )
    )
      return;
    const { versionKey, newName } = data;
    editorStore.editVersionName(versionKey, newName);
  };

  const onReorderVersion = (data) => {
    if (madeIdenticalAction(ProjectEditorCommands.VERSION_ORDER, data))
      return;
    editorStore.updateVersionOrder(data.newOrder);
  };
  
  const onVersionLock = (data) => {
    editorStore.setEditionLock(data.versionKey, data.locked);
  }
  
  const onVersionVisibility = (data) => {
    editorStore.setVersionHidden(data.versionKey, data.visible);
  }
  
  const onVersionWorkTypesUpdate = ({ workTypeIds }) => {
    editorStore.updateCustomWorkTypeIds(workTypeIds);
    editorStore.currentEstimateStructure?.historyManager.withoutUndo(() => {
      editorStore.currentEstimateStructure.setWorkTypeIdsFromDB(workTypeIds);
    })
  }
  
  const onProjectMetadataUpdate = ({ metadata, customWorkTypesWereCreated }) => {
    if(metadata?.workTypes.length)
      editorStore.addCustomEstimateWorkTypes(metadata.workTypes, customWorkTypesWereCreated);
  }

  const onProjectRename = (data) => {
    if (madeIdenticalAction(ProjectCommands.PROJECT_RENAME, data)) return;
    editorStore.setEstimateName(data.newName);
  };
  
  const onProjectRemove = ({ projectUuid }) => {
    if(projectUuid !== projectJoined.current.projectUuid)
      return;
    editorStore.setEstimateStatus(PROJECT_STATUS.REMOVED);
  }
  
  const onProjectArchive = ({ projectUuid }) => {
    if(projectUuid !== projectJoined.current.projectUuid)
      return;
    editorStore.setEstimateStatus(PROJECT_STATUS.ARCHIVE);
  }
  
  const onProjectStatusUpdate = ({ projectUuid, status }) => {
    if(projectUuid !== projectJoined.current.projectUuid)
      return;
    editorStore.setEstimateStatus(status);
  }
  
  const onProjectUserAdd = ({ projectUuid, ...userData }) => {
    if(projectUuid !== projectJoined.current.projectUuid)
      return;
    
    const observer = [...editorStore.estimateUsers.observer];
    observer.push(userData)
    editorStore.setEstimateUsers({
      ...editorStore.estimateUsers,
      observer
    })
  }
  
  const onProjectUserPromote = ({ projectUuid, userUuid }) => {
    if(projectUuid !== projectJoined.current.projectUuid)
      return;
    
    const newOwner = editorStore.estimateUsers.observer.find((u) => u.uuid === userUuid);
    const observer = [...editorStore.estimateUsers.observer].filter((u) => u.uuid !== userUuid);
    if (editorStore.estimateUsers.owner && editorStore.estimateUsers.owner?.uuid !== editorStore.estimateUsers.author.uuid)
      observer.push({ ...editorStore.estimateUsers.owner });
    
    editorStore.setEstimateUsers({
      ...editorStore.estimateUsers,
      owner: newOwner,
      observer
    });
  };
  
  const onProjectUserRemove = ({ projectUuid, userUuid }) => {
    if(projectUuid !== projectJoined.current.projectUuid)
      return;
    
    let newOwner = editorStore.estimateUsers.owner;
    const observer = [...editorStore.estimateUsers.observer].filter((u) => u.uuid !== userUuid);
    if (newOwner && userUuid === newOwner.uuid) {
      observer.push({ ...newOwner });
      newOwner = editorStore.estimateUsers.author;
    }
    
    editorStore.setEstimateUsers({
      ...editorStore.estimateUsers,
      owner: newOwner,
      observer
    });
  };

  const onAddNewWorkType = ({ id, name, backgroundColor }) => {
    editorStore.addCustomWorkType(id, name, backgroundColor);
  };

  const onWorkTypeChange = ({ id, name, backgroundColor }) => {
    editorStore.updateWorkTypeData(id, name, backgroundColor);
  };

  const onWorkTypeRemove = ({ id }) => {
    editorStore.removeWorkType(id);
  };

  const onWorkTypeAssignment = ({ workTypeId, userUuid }) => {
    editorStore.addLocalPermit(workTypeId, userUuid);
  };

  const onWorkTypeRevoke = ({ workTypeId, userUuid }) => {
    editorStore.removeLocalPermit(workTypeId, userUuid);
  };

  const onWorkTypeStatus = ({ workTypeId, status }) => {
    editorStore.updateLocalPermit(workTypeId, status);
  };

  const onUserWorkTypesStatus = ({ userId, status }) => {
    editorStore.setEstimateUserWorkTypeVisibility(userId, status);
  };

  const onCoverAdd = ({ cover }) => {
    editorStore.setPdfDocument(cover);
  };

  const onCoverRemove = () => {
    editorStore.removeDocument();
  };

  const onCoverUsedPagesUpdate = ({ docId, usedPages, tablePos }) => {
    editorStore.setUsedPages(usedPages, docId);
    if(tablePos)
      editorStore.setTablePos(tablePos, docId);
  };

  const onCoverScaleUpdate = ({ scale }) => {
    editorStore.setScale(scale.scale, scale.scaleValue, false);
  };
  
  const onShareSettingsUpdate = ({ settings }) => {
    editorStore.setAllShareLinkSettings(JSON.parse(settings));
  };
  
  // --------- AGENT SESSION ---------
  const onAgentSessionStart = ({ actor, tool, startedAt, idleTtlSeconds }) => {
    editorStore.setAgentSession({ actorName: actor?.name, tool, startedAt, idleTtlSeconds });
  };
  
  const onAgentSessionEnd = async ({ reason, takenOverBy }) => {
    editorStore.setAgentSession(null);
    enqueueSnackbar(
      reason === "taken_over"
        ? t("alerts.editor.agent_session.taken_over", { name: takenOverBy?.name })
        : t("alerts.editor.agent_session.finished"),
      { variant: "info", autoHideDuration: 5000 }
    );
    await resetProjectData();
  };
  
  const requestAgentSessionTakeover = () => {
    if(!projectJoined.current || !socket.current?.connected)
      return;
    socket.current.emit(ProjectEditorCommands.AGENT_SESSION_TAKEOVER, {
      projectUuid: projectJoined.current.projectUuid,
    });
  };
  
  // --------- AI GENERATOR ---------
  const onProjectAiGeneratorStart = ({ generatedByOtherUser }) => {
    console.log("GENERATOR STARTED!", generatedByOtherUser);
    if(generatedByOtherUser)
      editorStore.setAiGenerationAlert(true);
    editorStore.setAiGenerationStatus(true);
  }
  
  const onProjectAiGeneratorEnd = ({ fileReaderResults }) => {
    console.log("GENERATOR FINISHED!");
    editorStore.setAiGenerationAlert(false);
    editorStore.setAiGenerationStatus(false);
    editorStore.setAiGenerationSuccessAlert(true, fileReaderResults);
  }
  
  const onProjectAiGeneratorReasoning = ({ token }) => {
    console.log("REASONING:", token);
  }
  
  const onProjectLabelRemove = ({ labelId }) => {
    // @note: this happens when user removes unlisted or project label
    editorStore.removeCustomLabel(labelId);
    if (editorStore.currentEstimateStructure) {
      try {
        editorStore.currentEstimateStructure.historyManager.withoutUndo(() => {
          editorStore.currentEstimateStructure.purgeLabel(labelId);
        })
      } catch(e) {
        console.log("Error: applyPatch", e);
      }
    }
  }
  
  const onCompanyLabelRemove = ({ labelId, estimateUsesLabel }) => {
    // @note: this happens when user removes company label
    const label = editorStore.removeCompanyLabel(labelId);
    if(estimateUsesLabel)
      editorStore.addCustomLabel(label);
  }
  
  const onCompanyLabelCreate = ({ labelId, labelData }) => {
    editorStore.addCompanyLabel({ id: labelId, ...labelData });
  }
  
  const onProjectLabelCreate = ({ labelId, labelData }) => {
    editorStore.addCustomLabel({ id: labelId, ...labelData });
  }
  
  const onLabelUpdate = ({ labelId, labelData, isProjectLabel }) => {
    editorStore[isProjectLabel ? "updateCustomLabel" : "updateCompanyLabel"](
      labelId, labelData
    )
    
    if (editorStore.currentEstimateStructure)
      // @note: this happens when user removes unlisted or project label
      try {
        editorStore.currentEstimateStructure.historyManager.withoutUndo(() => {
          editorStore.currentEstimateStructure.updateLabel(labelId, labelData);
        })
      } catch(e) {
        console.log("Error: applyPatch", e);
      }
  }
  
  // --------- USER ACTIONS ---------

  const sendMoveData = (
    clientX,
    clientY,
    elementType,
    elementPath,
    elementId
  ) => {
    sendAction(ProjectEditorCommands.USER_MOVE, {
      id: userStore.data.id,
      vW: window.innerWidth,
      // vH: containerHeight,
      x: clientX,
      y: clientY,
      elementType,
      elementPath,
      elementId,
    });
  };
  
  const requestStructurePatch = (patches, metadata) => {
    saveAndSendAction(ProjectEditorCommands.PATCH, {
      versionKey: editorStore.currentVersionKey,
      timestamp: new Date().getTime(),
      patches,
      metadata,
    });
  };
  
  const requestCommentPatch = (patches) => {
    saveAndSendAction(ProjectEditorCommands.COMMENT_PATCH, {
      versionKey: editorStore.currentVersionKey,
      timestamp: new Date().getTime(),
      patches,
    });
  };

  const requestExistingVersion = (versionKey, rememberScroll) => {
    saveAndSendAction(ProjectEditorCommands.VERSION_REQUEST, {
      projectUuid: projectJoined.current.projectUuid,
      userUuid: userStore.userUuid,
      versionKey,
    });
    rememberScrollRef.current = rememberScroll;
  };
  
  const requestNewVersion = (copyComments, rememberScroll) => {
    saveAndSendAction(ProjectEditorCommands.VERSION_NEW, {
      originVersionKey: editorStore.currentVersionKey,
      copyComments,
    });
    rememberScrollRef.current = rememberScroll;
  };
  
  const requestNewImportedVersion = (structure, workTypes) => {
    setXlsImportOngoing(true);
    saveAndSendAction(ProjectEditorCommands.VERSION_IMPORT, { structure, workTypes });
  };

  const requestVersionRename = (versionKey, newName) => {
    saveAndSendAction(ProjectEditorCommands.VERSION_RENAME, { versionKey, newName });
  };

  const requestVersionRemoval = async (versionKey, rememberScroll) => {
    saveAndSendAction(ProjectEditorCommands.VERSION_REMOVE, { versionKey });
    commentStore.removeCommentByVersion(versionKey);
    await removeProjectVersionCommentByPath( // remove version comments
      projectJoined.current.projectUuid,
      userStore.data.uuid,
      versionKey, "",
    );
    rememberScrollRef.current = rememberScroll;
  };

  const requestVersionVisibility = (versionKey, visible) => {
    saveAndSendAction(ProjectEditorCommands.VERSION_VISIBILITY, { versionKey, visible });
  };

  const requestVersionLock = (versionKey, locked) => {
    saveAndSendAction(ProjectEditorCommands.VERSION_LOCK, { versionKey, locked });
  };

  const requestVersionReorder = (movedVersionKey, newOrder) => {
    saveAndSendAction(ProjectEditorCommands.VERSION_ORDER, { movedVersionKey, newOrder });
  };

  const addNewPermit = (workTypeId, userUuid) => {
    saveAndSendAction(ProjectEditorCommands.PERMIT_ADD, { workTypeId, userUuid });
  };

  const removePermit = (workTypeId, userUuid) => {
    saveAndSendAction(ProjectEditorCommands.PERMIT_REMOVE, { workTypeId, userUuid });
  };

  const setEstimateUserWorkTypeVisibility = (userId, status) => {
    saveAndSendAction(ProjectEditorCommands.PERMIT_USER_VISIBILITY, { userId, status: status ? 1 : 0 });
  };

  const setPermitStatus = (workTypeId, status) => {
    saveAndSendAction(ProjectEditorCommands.PERMIT_STATUS, { workTypeId, status });
  };

  const addPdfCover = (cover) => {
    saveAndSendAction(ProjectEditorCommands.COVER_ADD, {
      projectUuid: projectJoined.current.projectUuid,
      cover,
    });
  };

  const removeCover = () => {
    saveAndSendAction(ProjectEditorCommands.COVER_REMOVE, {
      projectUuid: projectJoined.current.projectUuid,
    });
  };
  
  const saveCoverChanges = async ({ usedPages, tablePos, scale }) => {
    if(!editorStore?.pdfDocument?.id)
      return;
    
    return await (
      saveProjectCoverQuery(
        projectJoined.current.projectUuid,
        userStore.userUuid,
        editorStore.pdfDocument.id,
        {
          ...editorStore.pdfDocument.file,
          usedPages: JSON.stringify(usedPages || editorStore.usedPdfPages),
          tablePos: tablePos || editorStore.proposalTablePosition,
          scale: scale || editorStore.scale,
        }
      )
    );
  }

  const updateCoverPages = async (docId, usedPages, tablePos) => {
    await saveCoverChanges({ usedPages, tablePos });
    saveAndSendAction(ProjectEditorCommands.COVER_PAGES, { docId, usedPages, tablePos });
  };

  const updateCoverScale = async (docId, scaleObj) => {
    await saveCoverChanges({ scale: scaleObj.scale });
    saveAndSendAction(ProjectEditorCommands.COVER_SCALE, { docId, scaleObj });
  };
  
  const requestCommentAdd = async (commenterData, path, name, body, mentions, internal, parentUuid) => {
    const versionKey = editorStore.currentVersionKey;
    const commentData = await createProjectVersionComment(
      projectJoined.current.projectUuid,
      commenterData.uuid, versionKey, path, name, body, mentions, internal, parentUuid
    );
    return {
      uuid: commentData.uuid,
      parentUuid,
      body,
      createdAt: commentData.createdAt,
      user: commenterData,
      isRead: true,
    }
  };
  
  const requestCommentEdit = async (commentUuid, commenterUuid, version, body, mentions) => {
    await editProjectVersionComment(commentUuid, projectJoined.current.projectUuid, commenterUuid, version, body, mentions);
  };
  
  const requestCommentRemove = async (commentUuid, userUuid) => {
    const versionKey = editorStore.currentVersionKey;
    await removeProjectVersionComment(
      commentUuid, projectJoined.current.projectUuid, userUuid, versionKey
    );
  };
  
  const requestCommentPathRemove = async (path) => {
    const versionKey = editorStore.currentVersionKey;
    commentStore.removeCommentByPath(path);
    await removeProjectVersionCommentByPath(
      projectJoined.current.projectUuid,
      userStore.data.uuid,
      versionKey, path,
    );
  };
  
  const requestCommentMove = async (oldPath, path) => {
    const versionKey = editorStore.currentVersionKey;
    commentStore.updateCommentPath(oldPath, path);
    await moveProjectVersionComment(
      projectJoined.current.projectUuid,
      userStore.data.uuid,
      versionKey,
      oldPath,
      path,
    );
  };
  
  const requestCommentPathElementRename = async (path, taskName) => {
    const versionKey = editorStore.currentVersionKey;
    commentStore.updateCommentElementName(path, taskName);
    await renameProjectVersionCommentElementQuery(
      projectJoined.current.projectUuid,
      userStore.data.uuid,
      versionKey,
      path,
      taskName,
    );
  };
  
  const requestShareSettingsUpdate = (settings) => {
    saveAndSendAction(ProjectEditorCommands.SHARE_SETTINGS, {
      settings: JSON.stringify(settings)
    });
  };
  
  const requestVersionWorkTypesChange = async (updatedWorkTypes) => {
    for(let wT of updatedWorkTypes) {
      const t = await createCustomWorkTypeQuery(wT.name, wT.color);
      wT.newId = t.id;
      requestWorkTypeCreate(t);
    }
    
    const workTypeIds = updatedWorkTypes
      .filter(wT => wT.oldId && wT.newId)
      .map(({ oldId, newId }) => ({ oldId, newId }));
    
    onVersionWorkTypesUpdate({ workTypeIds });
    saveAndSendAction(ProjectEditorCommands.VERSIONS_WORK_TYPE_UPDATE, { workTypeIds });
  }
  
  const requestAiGeneratorStop = () => {
    saveAndSendAction(ProjectEditorCommands.GENERATOR_STOP, {
      projectUuid: projectJoined.current.projectUuid
    });
  }
  
  const requestEstimateLabelCreate = (labelData) => {
    saveAndSendAction(ProjectEditorCommands.LABEL_CREATE, {
      projectUuid: projectJoined.current.projectUuid,
      labelId: labelData.id,
      labelData
    });
  }
  
  const requestEstimateLabelRemove = (labelSystemId) => {
    saveAndSendAction(ProjectEditorCommands.LABEL_REMOVE, {
      projectUuid: projectJoined.current.projectUuid,
      labelId: labelSystemId,
    });
  }
  
  const requestLabelUpdate = (labelSystemId, labelData, isProjectLabel) => {
    saveAndSendAction(CoreCommands.LABEL_UPDATE, {
      projectUuid: projectJoined.current.projectUuid,
      labelId: labelSystemId,
      labelData,
      isProjectLabel,
    });
  }

  const value = {
    users,
    connectionOnline: isSetup && connectionOnline,
    hasConnectionError,
    isXlsImportOngoing,
    editionPrivileges: joinEdition,
    initSocket: init,
    closeEstimate,
    joinEstimate: joinProject,
    
    sendMoveData,
    requestEstimateRename: requestProjectRename,
    addNewPermit,
    removePermit,
    setEstimateUserWorkTypeVisibility,
    setPermitStatus,
    addPdfCover,
    removeCover,
    updateCoverPages,
    updateCoverScale,
    
    requestStructurePatch,
    requestCommentPatch,
    requestExistingVersion,
    requestNewVersion,
    requestNewImportedVersion,
    requestVersionRename,
    requestVersionRemoval,
    requestVersionReorder,
    requestVersionVisibility,
    requestVersionLock,
    
    requestCommentAdd,
    requestCommentEdit,
    requestCommentRemove,
    requestCommentPathRemove,
    requestCommentMove,
    requestCommentPathElementRename,
    
    requestShareSettingsUpdate,
    requestVersionWorkTypesChange,
    requestWorkTypeChange,
    requestAiGeneratorStop,
    requestAgentSessionTakeover,
    requestEstimateLabelCreate,
    requestEstimateLabelRemove,
    requestLabelUpdate,
    requestCompanyLabelCreate,
    requestCompanyLabelRemove,
    navigateAndScroll,
  };

  return (
    <EditorSocketContext.Provider value={value}>
      {children}
    </EditorSocketContext.Provider>
  );
};

EditorSocketProvider.propTypes = {
  children: node.isRequired,
  reloadProjectData: func,
  proposalSocket: bool,
}
